import { memo, useState } from 'react';
import { ManterComNotaModal } from '@/components/kanban/ManterComNotaModal';
import { Button } from '@/components/ui/button';
import { Check, Eye, Package, MessageSquare, AlertTriangle, Loader2, PackagePlus } from 'lucide-react';
import { usePainelPedido } from '@/contexts/PainelPedidoContext';
import { CancelarItemTriagemButton } from '@/components/kanban/triagem/triagemPedidoUtils';
import { AcoesTriagem, BotaoManter } from '@/components/kanban/triagem/AcoesTriagem';
import { TriagemStandardCells } from './TriagemStandardColumns';
import type { ItemTriagemAtivacao } from './useTriagemAtivacao';
import type { TriagemStandardItem } from './TriagemStandardColumns';
import { TRIAGEM_BTN, TRIAGEM_BTN_PRIMARIO } from '@/components/kanban/triagem/triagemTabela';

interface TriagemAtivacaoRowProps {
  item: ItemTriagemAtivacao;
  onFinalizar: () => void;
  onAguardarNF?: () => void;
  onSmsEnviado: () => void;
  onCpfCnpjDivergente: () => void;
  onPedidoComplementar?: () => void;
  onMoverParaFinal: () => void;
  isFinalizando: boolean;
  isMovingToAguardandoNF: boolean;
  isMovingToSmsEnviado: boolean;
  isMovingToCpfCnpjDivergente: boolean;
  isMovingToFinal: boolean;
  showAguardarNF?: boolean;
  /** Fila que exige justificativa para manter o item parado (e a mostra na linha). */
  exigeJustificativa?: boolean;
  /** Manter pede justificativa, sem a coluna da justificativa na linha. */
  manterJustificado?: boolean;
  onAfterCancel?: () => void;
}

export const TriagemAtivacaoRow = memo(function TriagemAtivacaoRow({
  item,
  onFinalizar,
  onAguardarNF,
  onSmsEnviado,
  onCpfCnpjDivergente,
  onPedidoComplementar,
  onMoverParaFinal,
  isFinalizando,
  isMovingToAguardandoNF,
  isMovingToSmsEnviado,
  isMovingToCpfCnpjDivergente,
  isMovingToFinal,
  showAguardarNF = true,
  exigeJustificativa = false,
  manterJustificado = false,
  onAfterCancel,
}: TriagemAtivacaoRowProps) {
  const pedeJustificativa = exigeJustificativa || manterJustificado;
  const [manterModalOpen, setManterModalOpen] = useState(false);
  const { open: openPedido } = usePainelPedido();
  const diasParado = item.entrada_coluna_em
    ? Math.floor((Date.now() - new Date(item.entrada_coluna_em).getTime()) / 86400000) : null;
  const isProcessing = isFinalizando || isMovingToAguardandoNF || isMovingToSmsEnviado || isMovingToCpfCnpjDivergente || isMovingToFinal;

  // Map to standard item interface
  const stdItem: TriagemStandardItem = {
    ...item,
    linhas_formatadas: item.linhas_formatadas || [],
    operadora_doadora: item.operadora_doadora || '-',
    plano_nome: item.plano_nome || '',
    faturamento_tipo: item.faturamento_tipo || '',
    agenda_tipo: item.agenda_tipo || '',
    data_agendamento: item.data_agendamento || '',
    cessionario_nome: item.cessionario_nome || '',
    cessionario_cpf: item.cessionario_cpf || '',
    numero_cotacao_operadora: item.numero_cotacao_operadora || '',
    item_seq: item.item_seq ?? null,
  };

  return (
    <tr className={`box-border h-[33px] border-b border-border/30 hover:bg-muted/30 transition-colors ${!item.is_first_of_cnpj ? 'bg-muted/10' : ''}`}>
      <TriagemStandardCells item={stdItem} />
      {exigeJustificativa && <td className="px-2 py-0 align-middle">
        {item.fila_justificativa
          ? <button type="button" className="block w-full truncate text-left text-[10px] text-foreground"
              title={item.fila_justificativa} onClick={() => setManterModalOpen(true)}>{item.fila_justificativa}</button>
          : <button type="button" className="block w-full truncate text-left text-[10px] text-primary"
              onClick={() => setManterModalOpen(true)}>Sem justificativa — dizer por quê</button>}
      </td>}
      <td className="px-2 py-0 align-middle">
        <AcoesTriagem
          manter={
            <>
              <BotaoManter
                title={pedeJustificativa
                  ? 'Manter — consultei e ainda não mudou nada. Justificar a permanência e mover para o fim da fila.'
                  : 'Manter — consultei e ainda não mudou nada. Vai para o fim da fila.'}
                onClick={() => setManterModalOpen(true)}
                disabled={isProcessing}
                carregando={isMovingToFinal}
              />
              <ManterComNotaModal
                open={manterModalOpen}
                onOpenChange={setManterModalOpen}
                pedidoId={item.pedido_id}
                clienteId={item.cliente_id || ''}
                onConfirm={onMoverParaFinal}
                justificarFila={pedeJustificativa ? {
                  itemId: item.item_id,
                  coluna: item.coluna_atual,
                  numeroPedido: item.numero_pedido,
                  razaoSocial: item.razao_social,
                  justificativaAtual: item.fila_justificativa,
                  diasParado,
                } : undefined}
              />
            </>
          }
          avancar={
            /* ⛔ PN não conclui aqui (Douglas 24/09/2026): portabilidade só vira
               (PN) CONCLUÍDO com data e hora agendadas, na fila Portabilidade
               Agendada — o banco recusa. Aqui a PN avança pelo SMS. */
            item.is_portabilidade ? (
              <Button
                size="icon" variant="ghost"
                className={TRIAGEM_BTN_PRIMARIO}
                title={`SMS Enviado (ANATEL) — portabilidade (${item.produto || 'PN'}) não conclui aqui: conclui na Portabilidade Agendada, com data e hora.`}
                onClick={onSmsEnviado}
                disabled={isProcessing}
              >
                {isMovingToSmsEnviado ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MessageSquare className="h-3.5 w-3.5" />}
              </Button>
            ) : (
              <Button
                size="icon" variant="ghost"
                className={TRIAGEM_BTN_PRIMARIO}
                title="Finalizar Pedido Ativo (100%)"
                onClick={onFinalizar}
                disabled={isProcessing}
              >
                {isFinalizando ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              </Button>
            )
          }
          desvios={
            <>
              <Button
                size="icon" variant="ghost"
                className={TRIAGEM_BTN}
                title={showAguardarNF && onAguardarNF ? 'Aguardar Nota Fiscal' : 'Só na fila Inspeção'}
                onClick={onAguardarNF}
                disabled={isProcessing || !(showAguardarNF && onAguardarNF)}
              >
                {isMovingToAguardandoNF ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Package className="h-3.5 w-3.5" />}
              </Button>
              <Button
                size="icon" variant="ghost"
                className={TRIAGEM_BTN}
                title={item.is_portabilidade ? 'Na portabilidade o SMS Enviado já é o avançar' : 'SMS Enviado é só para portabilidade (PN)'}
                onClick={onSmsEnviado}
                disabled
              >
                {isMovingToSmsEnviado && !item.is_portabilidade ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MessageSquare className="h-3.5 w-3.5" />}
              </Button>
              <Button
                size="icon" variant="ghost"
                className={TRIAGEM_BTN}
                title="CPF/CNPJ Divergente"
                onClick={onCpfCnpjDivergente}
                disabled={isProcessing}
              >
                {isMovingToCpfCnpjDivergente ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <AlertTriangle className="h-3.5 w-3.5" />}
              </Button>
              <Button
                size="icon" variant="ghost"
                className={TRIAGEM_BTN}
                title={onPedidoComplementar ? 'Pedido complementar — tratei o conflito e abri o chamado. A cotação vai para a Triagem Pedido Complementar com o chamado.' : 'Só na fila Nota Fiscal'}
                onClick={onPedidoComplementar}
                disabled={isProcessing || !onPedidoComplementar}
              >
                <PackagePlus className="h-3.5 w-3.5" />
              </Button>
            </>
          }
          ferramentas={
            <Button
              size="icon" variant="ghost"
              className={TRIAGEM_BTN}
              title="Analisar — Abrir Painel do Pedido (Pedido 360)"
              onClick={() => openPedido(item.pedido_id, 'visao360')}
            >
              <Eye className="h-3 w-3" />
            </Button>
          }
          cancelar={
            <CancelarItemTriagemButton
              pedidoId={item.pedido_id}
              itens={[{ item_id: item.item_id, produto: item.produto || item.numero_pedido, quantidade: 1, subtotal: 0 }]}
              disabled={isProcessing}
              disabledTitle="Aguarde a ação em andamento"
              onAfterCancel={() => onAfterCancel?.()}
            />
          }
        />
      </td>
    </tr>
  );
});
